import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import { FixedType } from 'rc-table/lib/interface';

import {
    COLOR_ITEM,
    FORMAT_DATE_FOR_SAVE,
    FORMAT_TIME,
    TABLE_FILTER_DATE,
    TABLE_FILTER_NUMBER,
    TABLE_FILTER_STRING,
} from '../const';
import {
    ActionsType,
    ExtractKeys,
    ItemType,
    MappingItemIdIndexType,
    StructureTableFieldType,
    TableColumnOptionsType,
    TableColumnSmxType,
    TableQueryStructureType,
    TableQueryType,
} from '../types';

export const getStructureColumn = <K,>(
    structure: TableQueryStructureType[],
    columnsLabel: { [key: string]: string },
    isAddColumnAction: boolean,
    paramSorter: ExtractKeys<K>[],
    fixedColumn?: { [key: string]: FixedType },
) => {
    const getColumns = (table: TableQueryStructureType[]) => {
        let indexSortMultiple = 1;

        if (!table || !table.length) {
            return [];
        }

        const flattenListColumn: TableColumnSmxType[] = [];

        const getSubColumn = (
            column: TableQueryStructureType,
            parentField: string = '',
        ): TableColumnSmxType | TableColumnSmxType[] | null => {
            const fieldAsString = `${parentField ? parentField + '.' : ''}${column.field}`;
            const titleDisplayUppercase = column.display
                ?.split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            if (!column?.nest) {
                const newColumn: TableColumnSmxType = {
                    title: columnsLabel?.[fieldAsString] || titleDisplayUppercase,
                    dataIndex: fieldAsString,
                    key: fieldAsString,
                    // render: (text: string) => <a>{text}</a>,
                    sorter:
                        !parentField && paramSorter.includes(fieldAsString as ExtractKeys<K>)
                            ? {
                                  compare: false,
                                  multiple: indexSortMultiple++,
                              }
                            : false,
                    width: 100,
                    ellipsis: true,
                    typeDecode: column.type_decode,
                    type: column.type,
                    editable: column.editable,
                    fixed: fixedColumn?.[fieldAsString],
                };

                flattenListColumn.push(newColumn);
                return newColumn;
            } else {
                const children = column?.nest
                    .map((subColumn) => {
                        return getSubColumn(subColumn, fieldAsString);
                    })
                    .filter((subColumn) => subColumn); // Filter out null values (when there are nested columns)

                const newCell = {
                    title: titleDisplayUppercase,
                    dataIndex: fieldAsString,
                    key: fieldAsString,
                    children,
                };

                // return [newCell] as TableColumnSmxType[];
                return newCell as TableColumnSmxType;
            }
        };

        table.forEach((column: TableQueryStructureType) => {
            getSubColumn(column);
        });

        const bufferReducedColumns: { [key: string]: TableColumnSmxType } = {};

        for (const column of flattenListColumn) {
            bufferReducedColumns[column.key] = column;
        }

        return Object.keys(columnsLabel).map((columnKey) => bufferReducedColumns[columnKey]);
    };

    const columns = getColumns(structure || []);

    const updatedColumns = (columns: TableColumnSmxType[]) => {
        if (isAddColumnAction) {
            columns.push({
                title: 'Action',
                key: 'action',
                dataIndex: 'action',
                typeDecode: 'Action',
                type: 'action',
                editable: false,
                width: 100,
                fixed: 'right',
            });
        }

        return columns;
    };

    return {
        columns: isAddColumnAction ? updatedColumns(columns) : columns,
    };
};

export const getDataRowColumn = (structure: TableQueryType, columns: TableColumnSmxType[]) => {
    const dataRows = structure.edges
        .map((edge) => edge.node)
        .map((row) => {
            if (!row.id) {
                console.error('Row does not have id. Your request should include field.id', row);
                return null;
            }

            const newRow: { [key: string]: string } = {
                key: row.id,
            };

            const createCell = (columnsList: TableColumnSmxType[]) => {
                for (const column of columnsList) {
                    if (column.children) {
                        createCell(column.children);
                        continue;
                    }

                    let value = row;

                    const arrayNameField = column.dataIndex.split('.');

                    if (value) {
                        for (const field of arrayNameField) {
                            if (!value) {
                                break;
                            }
                            value = value[field];
                        }
                    }

                    newRow[column.dataIndex] = value || '';
                }
            };

            createCell(columns);

            return newRow;
        })
        .filter((row) => row);

    return {
        dataRows: dataRows,
    };
};

export const actionsForRender = (actions: ActionsType[]) => {
    const expectedActions: ActionsType[] = ['edit', 'delete'];
    return expectedActions.filter((action) => actions.includes(action));
};

export const actionsForPanel = (actions: ActionsType[]) => {
    const expectedActions: ActionsType[] = [
        'filter',
        'sorter',
        'search',
        'columnsView',
        'export',
        'export-csv',
        'import-csv',
        'createNew',
    ];
    return expectedActions.filter((action) => actions.includes(action));
};

export const getValueByFieldType = (value: any, typeField?: StructureTableFieldType) => {
    switch (typeField) {
        case 'Date':
            return dayjs(value);
        case 'Time':
            return dayjs(value, 'HH:mm:ss');
        default:
            return value;
    }
};

export const getValueForSavingToServer = (value: any, typeField?: StructureTableFieldType) => {
    switch (typeField) {
        case 'Date':
            return (value as any)?.format(FORMAT_DATE_FOR_SAVE);
        case 'Time':
            return (value as any).format(FORMAT_TIME);
        default:
            return value;
    }
};

export const getDefaultValueByFieldType = (typeField?: StructureTableFieldType) => {
    switch (typeField) {
        case 'Color':
            return COLOR_ITEM;
        case 'Date':
            return DateTime.now().toFormat(FORMAT_DATE_FOR_SAVE);
        case 'Time':
            return DateTime.now().toFormat(FORMAT_TIME);
        default:
            return '';
    }
};

export const getItemsListForFilter = (
    options: TableColumnOptionsType[],
    columnsKeyAsFilter: string[],
    handleMenuClick: (option: TableColumnOptionsType) => () => void,
) => {
    return options
        .filter((opt) => columnsKeyAsFilter.includes(opt.key))
        .map((option) => {
            const newOption = {
                ...option,
            };

            delete newOption.typeField;

            return {
                ...newOption,
                onClick: handleMenuClick(option),
            };
        });
};

export const getDefaultListFilter = (typeField: StructureTableFieldType) => {
    switch (typeField) {
        case 'String':
            return TABLE_FILTER_STRING;
        case 'Int':
            return TABLE_FILTER_NUMBER;
        case 'Date':
            return TABLE_FILTER_DATE;
        default:
            return TABLE_FILTER_STRING;
    }
};

export const getExpandedItems = (items: ItemType[], mappingItemIdIndex: MappingItemIdIndexType) => {
    return items.filter((item) => isItemExpanded(item, items, mappingItemIdIndex));
};

export const isItemExpanded = (
    item: ItemType,
    items: ItemType[],
    mappingItemIdIndex: MappingItemIdIndexType,
): boolean => {
    if (item.hideForCollapse) {
        return false;
    }

    if (!item.parentId) {
        return true;
    }

    const parentIndex = mappingItemIdIndex[item.parentId];
    const parent = items[parentIndex];

    return isItemExpanded(parent, items, mappingItemIdIndex);
};
